import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Sync Tasks From Rows
 *
 * Keeps one task type's open tasks equal to the rows an upstream query
 * returned (usually Famous SQL Query): opens a task per new row, refreshes
 * open tasks whose row changed, and closes open tasks whose row is gone with
 * the configured outcome. Tasks are matched to rows by the key column, so a
 * customer defines a task type and its feed without code.
 *
 * When the task type is anchored to an entity (a sales order, invoice,
 * customer, ...), each task is linked to the record whose match field (by
 * default `originId`, the ERP's id) equals the row's key, optionally only
 * from one source system. A row whose record isn't found yet still gets a
 * task; it links on a later run once the record has synced.
 *
 * A task someone closed by hand is never reopened. Closing is skipped (and
 * the output says why) when the query hit its row cap, returned nothing
 * unexpectedly, or would close more tasks than allowed in one run.
 */
export const syncTasksFromRowsStep = defineStepConfig({
  type: "sync_tasks_from_rows",
  name: "Sync Tasks From Rows",
  description:
    "Open, refresh and close a task type's tasks so they match the rows of an upstream query.",
  category: "Tasks",

  inputSchema: z.object({
    rows: z
      .array(z.record(z.string(), z.unknown()))
      .describe("Rows to sync, e.g. the Famous SQL Query's rows."),
    truncated: z
      .boolean()
      .optional()
      .describe(
        "Wire from the query's Rows Truncated output. When true, no task is closed this run.",
      ),
  }),

  outputSchema: z.object({
    reason: z
      .string()
      .nullable()
      .describe("Why the sync held back closes, if it did."),
    opened: z.number().int().nonnegative().describe("Tasks opened."),
    updated: z
      .number()
      .int()
      .nonnegative()
      .describe("Open tasks refreshed because their row changed."),
    closed: z
      .number()
      .int()
      .nonnegative()
      .describe("Open tasks closed because their row is gone."),
    unchanged: z.number().int().nonnegative(),
    skippedDismissed: z
      .number()
      .int()
      .nonnegative()
      .describe("Rows whose task someone closed by hand (left closed)."),
    skippedDuplicateKey: z.number().int().nonnegative(),
    skippedMissingKey: z.number().int().nonnegative(),
    failed: z
      .number()
      .int()
      .nonnegative()
      .describe("Writes that failed and were skipped (see the run logs)."),
    unresolvedAnchor: z
      .number()
      .int()
      .nonnegative()
      .describe(
        "Rows whose record (the task type's anchor) wasn't found, so their task isn't linked to it.",
      ),
    closesSuppressed: z
      .number()
      .int()
      .nonnegative()
      .describe("Tasks that would have closed but were held back by a guard."),
  }),

  configSchema: z.object({
    taskTypeKey: z
      .string()
      .default("")
      .describe("Task type whose tasks this step keeps in sync."),
    keyColumn: z
      .string()
      .default("")
      .describe(
        "Row column that identifies the record, e.g. ORDER_NUMBER; alias the column in the query to give it a friendly name. Values are trimmed and nothing else.",
      ),
    titleTemplate: z
      .string()
      .default("")
      .describe("Task title; {COLUMN} is replaced with the row's value."),
    descriptionTemplate: z
      .string()
      .default("")
      .describe("Optional task description, same placeholders."),
    inputMapping: z
      .array(z.object({ field: z.string().min(1), column: z.string().min(1) }))
      .default([])
      .describe("Which row column fills each of the task type's input fields."),
    closeOutcome: z
      .string()
      .default("")
      .describe("Outcome recorded on tasks closed because their row is gone."),
    refreshOpenTasks: z
      .boolean()
      .default(true)
      .describe("Update open tasks' title and fields when their row changes."),
    closeOnEmptyResult: z
      .boolean()
      .default(false)
      .describe("Close every open task when the query returns no rows."),
    matchField: z
      .string()
      .default("")
      .describe(
        "Field of the task type's anchor record that holds the key, e.g. originId (the ERP's id for synced records). Blank means originId, and records without that field are left unlinked.",
      ),
    sourceSystem: z
      .string()
      .default("famous")
      .describe(
        "Only link records synced from this system (their originSystem), e.g. famous. Leave empty to match any system. Ignored when the record type has no originSystem field; records with a blank originSystem don't match a named system.",
      ),
    maxClosesPerRun: z
      .number()
      .int()
      .positive()
      .optional()
      .describe("Hold back all closes when more than this many would close."),
  }),

  // Tasks it opens or closes publish their own events through the task service.
  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
  },
});
